import React, { useEffect, useRef } from 'react';
import MailBody from './InboxView/components/MailBody';

const asAddress = (value) => {
  if (!value) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  return String(value);
};

const formatWhen = (email, item) => {
  const raw = email?.timestamp?.toDate?.() || email?.date || email?.sentAt || item?.at;
  const t = raw instanceof Date ? raw.getTime() : (typeof raw === 'number' ? raw : Date.parse(raw || ''));
  if (!Number.isFinite(t) || !t) return '';
  return new Date(t).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const subjectOf = (view) => {
  const fromMail = view.email && view.email.subject;
  if (fromMail && fromMail !== '(no subject)') return fromMail;
  if (view.item && view.item.subject) return view.item.subject;
  const title = String((view.item && view.item.title) || '');
  const stripped = title.replace(/^Email sent:\s*/, '').trim();
  return stripped || 'Sent email';
};

/**
 * The stored outgoing copy, rendered the same way the sent mailbox renders it:
 * raw HTML in an isolated frame, so the footer and images stay as they left.
 */
export default function RecordedMail({ view, onClose }) {
  const panelRef = useRef(null);
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const email = view.email;
  const subject = subjectOf(view);
  const when = formatWhen(email, view.item);
  const from = asAddress(email && email.from);
  const to = asAddress(email && (email.to || email.address));
  const cc = asAddress(email && email.cc);

  return (
    <div className="ir-mail-overlay" onClick={onClose}>
      <div
        className="ir-mail"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ir-mail-subject"
        tabIndex={-1}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ir-mail-head">
          <div className="ir-mail-head-top">
            <div id="ir-mail-subject" className="ir-mail-subject">{subject}</div>
            <button type="button" className="ir-close" onClick={onClose} aria-label="Close email">
              <i className="fa fa-times" />
            </button>
          </div>
          {(from || to || cc || when) && (
            <div className="ir-mail-meta">
              {from && <div className="ir-mail-meta-row"><span>From</span><span>{from}</span></div>}
              {to && <div className="ir-mail-meta-row"><span>To</span><span>{to}</span></div>}
              {cc && <div className="ir-mail-meta-row"><span>Cc</span><span>{cc}</span></div>}
              {when && <div className="ir-mail-meta-row"><span>Date</span><span>{when}</span></div>}
            </div>
          )}
        </div>
        <div className="ir-mail-scroll">
          {view.status === 'loading' && <p className="ir-mail-status">Loading the sent email…</p>}
          {view.status === 'missing' && (
            <p className="ir-mail-status">
              This send is on the record, but the email itself was not stored, so it cannot be opened.
            </p>
          )}
          {view.status === 'error' && <p className="ir-mail-status">The sent email could not be loaded.</p>}
          {view.status === 'ready' && email && (
            <MailBody email={{ ...email, isSent: true, isOutgoing: true }} />
          )}
        </div>
      </div>
    </div>
  );
}

export function sentEmailFromRecord(mail, fallback = {}) {
  return {
    id: mail.docId || mail.id,
    subject: mail.subject || fallback.subject || '',
    from: mail.from || fallback.from || '',
    to: mail.to || mail.address || fallback.to || fallback.address || '',
    cc: (mail.cc && mail.cc.length ? mail.cc : fallback.cc) || [],
    date: mail.date || null,
    content: mail.html || mail.content || fallback.html || '',
    text: mail.text || fallback.text || '',
    isSent: true,
    isOutgoing: true,
  };
}
